import { seatRows } from "@/lib/format";
import { Armchair, CircleDot, DoorOpen, Bath, LifeBuoy, UserRound } from "lucide-react";

const FIXTURE = { door: [DoorOpen, "Door"], wc: [Bath, "WC"], driver: [LifeBuoy, "Driver"], staff: [UserRound, "Staff"] };

export function PlanCell({ c, size, children }) {
  const f = FIXTURE[c.t];
  if (c.t === "seat") return children || <div style={{ width: size, height: size }} className="grid place-items-center rounded-t-xl rounded-b-md border-2 border-emerald-200 bg-emerald-50 text-xs font-bold text-emerald-800">{c.n}</div>;
  if (!f) return <div style={{ width: size, height: size }} />;
  const [Ico, label] = f;
  return (
    <div style={{ width: size, height: size }} className="flex flex-col items-center justify-center rounded-lg bg-slate-100 text-slate-500" title={label}>
      <Ico className="h-4 w-4" /><span className="text-[8px] font-bold uppercase leading-none">{label}</span>
    </div>
  );
}

const Seat = ({ n, state, onClick, compact }) => {
  const styles = {
    free: "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 hover:-translate-y-0.5",
    selected: "bg-[#2563EB] border-[#2563EB] text-white shadow-[0_6px_14px_-6px_rgba(37,99,235,0.8)]",
    taken: "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed",
  };
  const size = compact ? "h-7 w-7 text-[10px]" : "h-11 w-11 sm:h-12 sm:w-12 text-xs";
  return (
    <button
      type="button"
      disabled={state === "taken" || !onClick}
      onClick={() => onClick?.(n)}
      className={`${size} relative grid place-items-center rounded-t-xl rounded-b-md border-2 font-bold transition-[transform,background-color,box-shadow] duration-150 ${styles[state]}`}
      data-testid={`seat-button-${n}`}
      aria-label={`Seat ${n} ${state}`}
    >
      {n}
    </button>
  );
};

export function SeatLegend() {
  return (
    <div className="flex flex-wrap gap-4 text-xs font-medium text-slate-600">
      <span className="flex items-center gap-1.5"><i className="h-3.5 w-3.5 rounded bg-emerald-50 border-2 border-emerald-200" />Available</span>
      <span className="flex items-center gap-1.5"><i className="h-3.5 w-3.5 rounded bg-[#2563EB]" />Selected</span>
      <span className="flex items-center gap-1.5"><i className="h-3.5 w-3.5 rounded bg-slate-100 border-2 border-slate-200" />Booked</span>
    </div>
  );
}

export default function SeatMap({ capacity, layout, plan, taken = [], selected = [], onToggle, compact = false }) {
  const state = (n) => (taken.includes(n) ? "taken" : selected.includes(n) ? "selected" : "free");
  if (plan) {
    const size = compact ? 28 : 46;
    return (
      <div className={`inline-block rounded-[28px] border-2 border-slate-200 bg-white ${compact ? "p-3" : "p-4 sm:p-6"}`} data-testid="seat-map">
        <div className="mb-3 border-b border-dashed border-slate-200 pb-2 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">Front</div>
        <div className="grid gap-1.5 sm:gap-2" style={{ gridTemplateColumns: `repeat(${plan.cols}, ${size}px)` }} data-testid="seat-plan-grid">
          {plan.cells.flatMap((row, r) => row.map((c, i) => (
            <PlanCell key={`${r}-${i}`} c={c} size={size}>{c.t === "seat" ? <Seat n={c.n} state={state(c.n)} onClick={onToggle} compact={compact} /> : null}</PlanCell>
          )))}
        </div>
      </div>
    );
  }
  const { per, left, rows } = seatRows(capacity, layout);
  const gap = compact ? "gap-1" : "gap-2";
  return (
    <div className={`inline-block rounded-[28px] border-2 border-slate-200 bg-white ${compact ? "p-3" : "p-4 sm:p-6"}`} data-testid="seat-map">
      <div className={`mb-3 flex items-center justify-between border-b border-dashed border-slate-200 ${compact ? "pb-2" : "pb-3"}`}>
        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400"><CircleDot className="h-3.5 w-3.5" /> Door</span>
        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Driver <Armchair className="h-4 w-4" /></span>
      </div>
      <div className={`flex flex-col ${gap}`}>
        {rows.map((row, i) => (
          <div key={i} className={`flex ${gap} ${row.bench ? "justify-between" : ""}`}>
            {row.bench
              ? row.seats.map((n) => <Seat key={n} n={n} state={state(n)} onClick={onToggle} compact={compact} />)
              : (
                <>
                  <div className={`flex ${gap}`}>{row.seats.slice(0, left).map((n) => <Seat key={n} n={n} state={state(n)} onClick={onToggle} compact={compact} />)}</div>
                  <div className={compact ? "w-4" : "w-8 sm:w-10"} />
                  <div className={`flex ${gap}`}>{row.seats.slice(left, per).map((n) => <Seat key={n} n={n} state={state(n)} onClick={onToggle} compact={compact} />)}</div>
                </>
              )}
          </div>
        ))}
      </div>
    </div>
  );
}
